import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js"

const CLIENT_FIELDS = ["name", "email", "phone"]

export default class extends Controller {
  static targets = [
    "cardElement",
    "error",
    "setupIntentId",
    "paymentIntentId",
    "skipPayment",
    "submitButton",
    "loading",
  ]

  static values = {
    publishableKey: String,
    stripeAccount: String,
    setupIntentUrl: String,
    paymentIntentUrl: String,
    mode: { type: String, default: "setup" },
  }

  async connect() {
    this.form = this.element.closest("form")
    this.stripe = null
    this.elements = null
    this.paymentElement = null
    this.clientSecret = null
    this.isConfirming = false
    this.isSubmittingAfterRedirect = false
    this.mountedClientSignature = null
    this.intentAttemptId = this.generateAttemptId()

    if (!this.form) {
      this.renderError("Booking form not found.")
      return
    }

    if (!this.hasPublishableKeyValue) {
      this.renderError("Stripe publishable key is missing.")
      return
    }

    if (this.isSetupMode && !this.hasSetupIntentUrlValue) {
      this.renderError("Stripe setup intent URL is missing.")
      return
    }

    if (this.isInstantMode && !this.hasPaymentIntentUrlValue) {
      this.renderError("Stripe payment intent URL is missing.")
      return
    }

    this.handleClientFieldChange = this.handleClientFieldChange.bind(this)

    try {
      this.stripe = await loadStripe(this.publishableKeyValue, this.stripeOptions)

      if (!this.stripe) {
        this.renderError("Failed to initialize Stripe.")
        return
      }

      this.bindClientFieldListeners()
      this.observeVisibility()
      await this.waitForBookingEventDetailsRestore()

      const handledRedirect = await this.handleRedirectReturn()
      if (handledRedirect) return

      await this.mountIfReady()
    } catch (error) {
      console.error("[stripe-payment] connect error:", error)
      this.renderError(error.message || "Failed to load payment form.")
    }
  }

  disconnect() {
    clearTimeout(this.clientFieldChangeTimeout)
    this.unbindClientFieldListeners()
    this.visibilityObserver?.disconnect()
    this.unmountPaymentElement()
  }

  get stripeOptions() {
    if (!this.hasStripeAccountValue) return {}

    return { stripeAccount: this.stripeAccountValue }
  }

  async confirm(event) {
    event.preventDefault()
    if (
      this.isConfirming ||
      this.isSubmittingAfterRedirect ||
      (this.isSetupMode && this.hasSetupIntentIdTarget && this.setupIntentIdTarget.value) ||
      (this.isInstantMode && this.hasPaymentIntentIdTarget && this.paymentIntentIdTarget.value)
    )
      return

    this.clearError()
    this.setSubmitting(true)

    try {
      const validationMessage = await this.bookingValidationMessage()
      if (validationMessage) {
        this.renderError(validationMessage)
        return
      }

      const ready = await this.mountIfReady()
      if (!ready) return

      const returnUrl = this.buildReturnUrl()

      const { error, intent } = await this.confirmStripeIntent(returnUrl)

      if (error) {
        this.renderError(this.mapStripeError(error))
        return
      }

      const intentId = intent?.id || this.extractIntentId(this.clientSecret)

      if (!intentId) {
        this.renderError(this.defaultErrorMessage)
        return
      }

      this.submitBookingForm(intentId)
    } catch (error) {
      console.error("[stripe-payment] confirm error:", error)
      this.renderError(error.message || this.defaultErrorMessage)
    } finally {
      if (!this.isSubmittingAfterRedirect) {
        this.setSubmitting(false)
      }
    }
  }

  skip(event) {
    event.preventDefault()
    this.clearError()

    if (this.hasSetupIntentIdTarget) this.setupIntentIdTarget.value = ""
    if (this.hasPaymentIntentIdTarget) this.paymentIntentIdTarget.value = ""
    if (this.hasSkipPaymentTarget) this.skipPaymentTarget.value = "1"

    this.form.requestSubmit()
  }

  async handleRedirectReturn() {
    const setupIntentClientSecret = this.urlParam("setup_intent_client_secret")
    const paymentIntentClientSecret = this.urlParam("payment_intent_client_secret")
    const redirectStatus = this.urlParam("redirect_status")

    if (!setupIntentClientSecret && !paymentIntentClientSecret) return false
    if (!this.stripe) return false

    this.clearError()
    this.setSubmitting(true)
    this.isSubmittingAfterRedirect = true

    try {
      const { intent, error } = await this.retrieveRedirectIntent(
        setupIntentClientSecret,
        paymentIntentClientSecret
      )

      if (error) {
        this.renderError(this.mapStripeError(error, intent))
        this.cleanupRedirectParams()
        return true
      }

      if (!intent) {
        this.renderError("Failed to verify payment.")
        this.cleanupRedirectParams()
        return true
      }

      if (intent.status === "succeeded") {
        const validationMessage = await this.bookingValidationMessage()
        if (validationMessage) {
          this.renderError(validationMessage)
          this.cleanupRedirectParams()
          return true
        }

        this.cleanupRedirectParams()
        this.submitBookingForm(intent.id)
        return true
      }

      if (intent.status === "processing") {
        this.renderError("Payment is still processing. Please try again in a moment.")
        this.cleanupRedirectParams()
        return true
      }

      if (intent.status === "requires_payment_method") {
        this.renderError(this.mapStripeError(null, intent))
        this.cleanupRedirectParams()
        return true
      }

      if (intent.status === "canceled") {
        this.renderError(this.mapStripeError(null, intent))
        this.cleanupRedirectParams()
        return true
      }

      if (redirectStatus === "failed") {
        this.renderError("Authentication failed. Please try again.")
        this.cleanupRedirectParams()
        return true
      }

      this.renderError(this.mapStripeError(null, intent))
      this.cleanupRedirectParams()
      return true
    } catch (error) {
      console.error("[stripe-payment] redirect return error:", error)
      this.renderError(error.message || "Failed to resume payment flow.")
      this.cleanupRedirectParams()
      return true
    } finally {
      this.setSubmitting(false)
      this.isSubmittingAfterRedirect = false
    }
  }

  async mountIfReady() {
    if (!this.isElementVisible()) return false

    this.clearError()

    const client = this.extractClientAttributes()

    if (!client.name || !client.email) {
      this.renderError("Please enter your name and email before payment.")
      return false
    }

    if (!this.isValidEmail(client.email)) {
      this.renderError("Please enter a valid email address before payment.")
      return false
    }

    await this.ensureMounted(client)
    return true
  }

  async bookingValidationMessage() {
    const bookingController = this.application.getControllerForElementAndIdentifier(
      this.form,
      "booking"
    )

    return bookingController?.paymentValidationMessage?.() || null
  }

  waitForBookingEventDetailsRestore() {
    if (this.form?.dataset.bookingEventDetailsRestored === "true") return Promise.resolve()

    return new Promise((resolve) => {
      let timeout
      const handleRestored = () => {
        clearTimeout(timeout)
        resolve()
      }

      timeout = setTimeout(() => {
        this.form?.removeEventListener("booking:event-details-restored", handleRestored)
        resolve()
      }, 500)

      this.form?.addEventListener("booking:event-details-restored", handleRestored, { once: true })
    })
  }

  async ensureMounted(client) {
    if (!this.stripe) throw new Error("Stripe is not initialized.")

    this.prepareBookingLineItemsForSubmit()

    const signature = this.clientSignature(client)
    if (this.mountedClientSignature === signature && this.paymentElement) return

    this.setLoading(true)

    try {
      this.unmountPaymentElement()

      const { clientSecret, stripeConnectedAccountId } = await this.fetchIntentPayload(client)
      if ((stripeConnectedAccountId || "") !== this.stripeAccountValue) {
        throw new Error("Stripe account does not match the payment intent.")
      }

      this.clientSecret = clientSecret

      this.elements = this.stripe.elements({
        clientSecret,
        appearance: { theme: "stripe" },
      })

      this.paymentElement = this.elements.create("payment", {
        layout: "tabs",
        defaultValues: {
          billingDetails: client,
        },
      })

      this.paymentElement.mount(this.cardElementTarget)
      this.mountedClientSignature = signature
    } finally {
      this.setLoading(false)
    }
  }

  async fetchIntentPayload(_clientAttributes) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const formData = new FormData(this.form)
    formData.append("payment_intent_attempt_id", this.intentAttemptId)

    const response = await fetch(this.intentUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      credentials: "same-origin",
      body: formData,
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      const message =
        data?.errors?.join(", ") || data?.error || "Failed to initialize payment form."
      throw new Error(message)
    }

    if (!data?.clientSecret) {
      throw new Error("Stripe client secret is missing.")
    }

    return data
  }

  prepareBookingLineItemsForSubmit() {
    const bookingController = this.application.getControllerForElementAndIdentifier(
      this.form,
      "booking"
    )

    bookingController?.prepareLineItemsForSubmit?.()
  }

  submitBookingForm(intentId) {
    if (!intentId) {
      this.renderError(this.defaultErrorMessage)
      return
    }

    if (this.isSetupMode && this.hasSetupIntentIdTarget) this.setupIntentIdTarget.value = intentId
    if (this.isInstantMode && this.hasPaymentIntentIdTarget)
      this.paymentIntentIdTarget.value = intentId
    if (this.hasSkipPaymentTarget) this.skipPaymentTarget.value = "0"

    this.form.requestSubmit()
  }

  async confirmStripeIntent(returnUrl) {
    if (this.isInstantMode) {
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl,
        },
      })

      return { error, intent: paymentIntent }
    }

    const { error, setupIntent } = await this.stripe.confirmSetup({
      elements: this.elements,
      redirect: "if_required",
      confirmParams: {
        return_url: returnUrl,
      },
    })

    return { error, intent: setupIntent }
  }

  async retrieveRedirectIntent(setupIntentClientSecret, paymentIntentClientSecret) {
    if (paymentIntentClientSecret) {
      const { paymentIntent, error } = await this.stripe.retrievePaymentIntent(
        paymentIntentClientSecret
      )
      return { intent: paymentIntent, error }
    }

    const { setupIntent, error } = await this.stripe.retrieveSetupIntent(setupIntentClientSecret)
    return { intent: setupIntent, error }
  }

  buildReturnUrl() {
    const url = new URL(window.location.href, window.location.origin)
    url.searchParams.set("payment_step", "payment")
    return url.toString()
  }

  cleanupRedirectParams() {
    const url = new URL(window.location.href, window.location.origin)
    const stripeParams = [
      "setup_intent",
      "setup_intent_client_secret",
      "payment_intent",
      "payment_intent_client_secret",
      "redirect_status",
      "payment_step",
    ]

    stripeParams.forEach((param) => url.searchParams.delete(param))
    window.history.replaceState({}, document.title, url.toString())
  }

  extractClientAttributes() {
    return {
      name: this.findClientValue("name"),
      email: this.findClientValue("email"),
      phone: this.findClientValue("phone"),
    }
  }

  findClientValue(fieldName) {
    const input = this.findClientInput(fieldName)
    return input?.value?.trim() || ""
  }

  findClientInput(fieldName) {
    const selectors = [
      `input[name="booking[client_attributes][${fieldName}]"]`,
      `input[name="booking[client][${fieldName}]"]`,
      `textarea[name="booking[client_attributes][${fieldName}]"]`,
      `textarea[name="booking[client][${fieldName}]"]`,
      `#booking_client_attributes_${fieldName}`,
      `[name*="[client_attributes]"][name$="[${fieldName}]"]`,
    ]

    return selectors.map((selector) => this.form?.querySelector(selector)).find(Boolean)
  }

  bindClientFieldListeners() {
    this.clientInputs = CLIENT_FIELDS.map((field) => this.findClientInput(field)).filter(Boolean)

    this.clientInputs.forEach((input) => {
      input.addEventListener("input", this.handleClientFieldChange)
      input.addEventListener("change", this.handleClientFieldChange)
      input.addEventListener("blur", this.handleClientFieldChange)
    })
  }

  unbindClientFieldListeners() {
    if (!this.clientInputs) return

    this.clientInputs.forEach((input) => {
      input.removeEventListener("input", this.handleClientFieldChange)
      input.removeEventListener("change", this.handleClientFieldChange)
      input.removeEventListener("blur", this.handleClientFieldChange)
    })
  }

  handleClientFieldChange() {
    if (this.isConfirming || this.isSubmittingAfterRedirect) return

    clearTimeout(this.clientFieldChangeTimeout)

    this.clientFieldChangeTimeout = setTimeout(async () => {
      try {
        await this.mountIfReady()
      } catch (error) {
        console.error("[stripe-payment] remount error:", error)
        this.renderError(error.message || "Failed to refresh payment form.")
      }
    }, 400)
  }

  observeVisibility() {
    this.visibilityObserver = new MutationObserver(() => {
      if (this.isConfirming || this.isSubmittingAfterRedirect) return

      this.mountIfReady()
    })
    this.visibilityObserver.observe(this.element, {
      attributes: true,
      attributeFilter: ["style", "class"],
    })
  }

  isElementVisible() {
    return this.element.offsetParent !== null
  }

  clientSignature(client) {
    if (this.isInstantMode) {
      return `${client.name}|${client.email}|${client.phone}|${this.bookingFormSignature()}`
    }

    return `${client.name}|${client.email}|${client.phone}`
  }

  bookingFormSignature() {
    const ignoredFields = new Set([
      "authenticity_token",
      "booking[stripe_setup_intent_id]",
      "booking[stripe_payment_intent_id]",
      "booking[skip_payment]",
    ])

    return Array.from(new FormData(this.form).entries())
      .filter(([name]) => !ignoredFields.has(name))
      .map(([name, value]) => [
        name,
        typeof File !== "undefined" && value instanceof File ? value.name : value.toString(),
      ])
      .sort(([leftName, leftValue], [rightName, rightValue]) =>
        `${leftName}=${leftValue}`.localeCompare(`${rightName}=${rightValue}`)
      )
      .map(([name, value]) => `${name}=${value}`)
      .join("&")
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  extractIntentId(clientSecret) {
    return clientSecret?.split("_secret_")?.[0] || null
  }

  generateAttemptId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  urlParam(name) {
    return new URLSearchParams(window.location.search).get(name)
  }

  unmountPaymentElement() {
    this.paymentElement?.unmount()
    this.paymentElement = null
    this.elements = null
    this.clientSecret = null
    this.mountedClientSignature = null
  }

  setSubmitting(state) {
    this.isConfirming = state

    if (!this.hasSubmitButtonTarget) return

    if (state) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.dataset.originalText ||= this.submitButtonTarget.textContent
      this.submitButtonTarget.textContent = "Saving..."
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent =
        this.submitButtonTarget.dataset.originalText || this.defaultSubmitText
    }
  }

  setLoading(state) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = state ? "flex" : "none"
    }

    if (!this.hasSubmitButtonTarget || this.isConfirming || this.isSubmittingAfterRedirect) return

    if (state) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.dataset.originalText ||= this.submitButtonTarget.textContent
      this.submitButtonTarget.textContent = "Loading payment form..."
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent =
        this.submitButtonTarget.dataset.originalText || this.defaultSubmitText
    }
  }

  renderError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = ""
  }

  mapStripeError(error, intent) {
    const message = error?.message?.toLowerCase() || ""

    if (
      message.includes("unable to authenticate your payment method") ||
      message.includes("authentication failed") ||
      message.includes("authentication was not completed")
    ) {
      return "We couldn’t verify your card. Please try again or use a different card."
    }

    if (error?.type === "card_error") {
      return "We couldn’t verify your card. Please try again or use a different card."
    }

    if (this.isInstantMode) return this.mapPaymentIntentError(intent)

    return this.mapSetupIntentError(intent)
  }

  mapPaymentIntentError(paymentIntent) {
    if (paymentIntent?.last_payment_error?.code === "authentication_required") {
      return "Authentication failed. Please try again."
    }

    if (paymentIntent?.last_payment_error?.message) {
      return paymentIntent.last_payment_error.message
    }

    if (paymentIntent?.status === "requires_payment_method") {
      return "Payment was not completed. Please try again or use a different card."
    }

    if (paymentIntent?.status === "canceled") {
      return "Payment was canceled. Please try again."
    }

    return `${this.defaultErrorMessage} Please try again.`
  }

  mapSetupIntentError(setupIntent) {
    if (setupIntent?.last_setup_error?.code === "authentication_required") {
      return "Authentication failed. Please try again."
    }

    if (setupIntent?.last_setup_error?.message) {
      return setupIntent.last_setup_error.message
    }

    if (setupIntent?.status === "requires_payment_method") {
      return "Card authentication was not completed. Please try again or use a different card."
    }

    if (setupIntent?.status === "canceled") {
      return "Card setup was canceled. Please try again."
    }

    return `${this.defaultErrorMessage} Please try again.`
  }

  get intentUrl() {
    return this.isInstantMode ? this.paymentIntentUrlValue : this.setupIntentUrlValue
  }

  get isInstantMode() {
    return this.modeValue === "instant"
  }

  get isSetupMode() {
    return !this.isInstantMode
  }

  get defaultSubmitText() {
    return this.isInstantMode ? "Pay and Confirm Booking" : "Save Card and Confirm Booking"
  }

  get defaultErrorMessage() {
    return this.isInstantMode ? "Failed to process payment." : "Failed to save card."
  }
}
